// Copyright (c) Meta Platforms, Inc. and affiliates.

'use client';

/**
 * @file DateRangeInput.tsx
 * @input Uses React, Field, Calendar (range mode), usePopover
 * @output Exports DateRangeInput component, DateRangeInputProps
 * @position Core implementation; consumed by index.ts, tested by DateRangeInput.test.tsx
 *
 * SYNC: When modified, update these files to stay in sync:
 * - /packages/core/src/DateRangeInput/DateRangeInput.doc.mjs (props table, features, implementation notes)
 * - /packages/core/src/DateRangeInput/DateRangeInput.test.tsx (tests for new/changed behavior)
 * - /packages/core/src/DateRangeInput/index.ts (exports if types change)
 * - /apps/storybook/stories/DateRangeInput.stories.tsx (storybook stories)
 * - /packages/cli/templates/blocks/components/DateRangeInput/ (showcase blocks)
 */
import { useId, useCallback, useMemo, useOptimistic, useTransition } from 'react';
import * as stylex from '@stylexjs/stylex';
import { plainDateFromISO, plainDateToday, plainDateFormat, DATE_FORMAT_SHORT, DATE_FORMAT_SHORT_WITH_YEAR } from "../utils/plainDate.js";
import "../theme/tokens.stylex.js";
import { colorVars, sizeVars, radiusVars, spacingVars, typographyVars, typeScaleVars, borderVars } from "../theme/tokens.stylex.js";
import { Field, inputWrapperStyles, inputStatusBorderStyles, inputStatusHoverShadowStyles, inputStatusFocusWithinStyles } from "../Field/index.js";
import { Icon } from "../Icon/index.js";
import { Spinner } from "../Spinner/index.js";
import { Calendar } from "../Calendar/index.js";
import { usePopover } from "../Popover/index.js";
import { useTooltip } from "../Tooltip/index.js";
import { mergeProps } from "../utils/index.js";
import { useSize } from "../SizeContext/SizeContext.js";
import { themeProps } from "../utils/themeProps.js";
import { useTranslator } from "../i18n/index.js";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const sizeStyles = {
  sm: {
    kZKoxP: "x6k0iem",
    k7Eaqz: "xfb3i0g",
    $$css: true
  },
  md: {
    kZKoxP: "x1ueg155",
    k7Eaqz: "xfb3i0g",
    $$css: true
  },
  lg: {
    kZKoxP: "xssyfek",
    k7Eaqz: "xfb3i0g",
    $$css: true
  }
};
function formatRangeDisplay(range) {
  if (!range) {
    return '';
  }
  const start = plainDateFromISO(range.start);
  const end = plainDateFromISO(range.end);
  const currentYear = plainDateToday().year;
  const sameYear = start.year === end.year && start.year === currentYear;
  const fmt = sameYear ? DATE_FORMAT_SHORT : DATE_FORMAT_SHORT_WITH_YEAR;
  return `${plainDateFormat(start, fmt)} – ${plainDateFormat(end, fmt)}`;
}
function isRangeEqual(a, b) {
  if (a === b) {
    return true;
  }
  if (!a || !b) {
    return false;
  }
  return a.start === b.start && a.end === b.end;
}
/**
 * A date range picker with a button trigger that opens a popover
 * containing a dual-month calendar and optional preset ranges.
 *
 * @example
 * ```
 * <DateRangeInput
 *   label="Date range"
 *   value={range}
 *   onChange={setRange}
 *   presets={[
 *     { label: "Last 7 days", getRange: () => ({start: "...", end: "..."}) },
 *   ]}
 * />
 * ```
 */
export function DateRangeInput({
  label,
  isLabelHidden = false,
  description,
  isOptional = false,
  isRequired = false,
  isDisabled = false,
  disabledMessage,
  value,
  onChange,
  changeAction,
  isLoading = false,
  min,
  max,
  dateConstraints,
  presets,
  hasClear = true,
  placeholder: placeholderFromProps,
  size: sizeProp,
  status,
  labelTooltip,
  numberOfMonths = 2,
  width,
  xstyle,
  className,
  style,
  ref,
  ...rest
}) {
  const t = useTranslator();
  const placeholder = placeholderFromProps ?? t('@astryx.dateRangeInput.placeholder');
  const size = useSize(sizeProp, 'md');
  const id = useId();
  const descriptionID = useId();
  const statusMessageID = useId();
  const [, startTransition] = useTransition();
  const [optimisticValue, setOptimisticValue] = useOptimistic(value);
  const isBusy = isLoading || optimisticValue !== value;
  const isEffectivelyDisabled = isDisabled || isBusy;

  // Disabled-reason tooltip. Disabled controls swallow pointer events, so the
  // tooltip listeners attach to the trigger container (which already exists)
  // and the trigger button stays perceivable via aria-disabled instead of the
  // disabled attribute. Activation is blocked by the isEffectivelyDisabled
  // guard in handleToggle. Only the persistent isDisabled state (not the
  // transient busy state) surfaces a reason.
  const showsDisabledMessage = isDisabled && !!disabledMessage;
  const disabledMessageTooltip = useTooltip({
    placement: 'above',
    // The container div is not naturally focusable; focusin bubbles up from
    // the trigger button, so always attach focus listeners.
    focusTrigger: 'always',
    isEnabled: showsDisabledMessage
  });
  const statusIconMap = {
    warning: 'warning',
    error: 'error',
    success: 'success'
  };
  const statusIconColorMap = {
    warning: 'warning',
    error: 'error',
    success: 'success'
  };
  const ariaDescribedBy = [description ? descriptionID : null, status?.message ? statusMessageID : null, showsDisabledMessage ? disabledMessageTooltip.describedBy : null].filter(Boolean).join(' ') || undefined;
  const displayValue = useMemo(() => formatRangeDisplay(optimisticValue), [optimisticValue]);
  const popover = usePopover({
    dialogLabel: t('@astryx.dateRangeInput.dialogLabel'),
    closeButtonLabel: t('@astryx.dateInput.closeCalendar')
  });
  const fireChange = useCallback(newValue => {
    if (isBusy) {
      return;
    }
    onChange(newValue);
    if (changeAction) {
      startTransition(async () => {
        setOptimisticValue(newValue);
        await changeAction(newValue);
      });
    }
  }, [isBusy, onChange, changeAction, startTransition, setOptimisticValue]);
  const handleToggle = useCallback(() => {
    if (!isEffectivelyDisabled) {
      if (popover.isOpen) {
        popover.hide();
      } else {
        popover.show();
      }
    }
  }, [isEffectivelyDisabled, popover]);
  const handleRangeSelect = useCallback(range => {
    fireChange(range);
    popover.hide();
  }, [fireChange, popover]);
  const handlePresetClick = useCallback(preset => {
    fireChange(preset.getRange());
    popover.hide();
  }, [fireChange, popover]);
  const handleClear = useCallback(e => {
    e.stopPropagation();
    fireChange(null);
  }, [fireChange]);
  const triggerAriaLabel = value ? `${label}: ${displayValue}` : `${label}: ${placeholder}`;
  return /*#__PURE__*/_jsxs(Field, {
    label: label,
    isLabelHidden: isLabelHidden,
    description: description,
    inputID: id,
    descriptionID: description ? descriptionID : undefined,
    isOptional: isOptional,
    isRequired: isRequired,
    isDisabled: isEffectivelyDisabled,
    status: status ? {
      type: status.type,
      message: status.message,
      messageID: status.message ? statusMessageID : undefined
    } : undefined,
    labelTooltip: labelTooltip,
    width: width,
    children: [/*#__PURE__*/_jsxs("div", {
      ref: el => {
        popover.triggerRef(el);
        // Anchor + hover/focus listeners for the disabled-message tooltip.
        // Handlers are gated internally by isEnabled, and anchor names
        // compose, so attaching unconditionally is safe.
        disabledMessageTooltip.ref(el);
      },
      ...rest,
      ...mergeProps(themeProps('date-range-input', {
        size,
        status: status?.type ?? null
      }), stylex.props(inputWrapperStyles.base, sizeStyles[size], isEffectivelyDisabled && inputWrapperStyles.disabled, status && inputStatusBorderStyles[status.type], status && inputStatusHoverShadowStyles[status.type], status && inputStatusFocusWithinStyles[status.type], xstyle), className, style),
      children: [/*#__PURE__*/_jsx("button", {
        type: "button",
        onClick: handleToggle,
        disabled: isEffectivelyDisabled,
        "aria-label": popover.isOpen ? t('@astryx.dateInput.toggleCalendarClose') : t('@astryx.dateInput.openCalendar'),
        tabIndex: -1,
        ...{
          0: {
            className: "x78zum5 x6s0dn4 xl56j7k x1717udv x1ghz6dp xc342km xng3xce xjbqb8w x1ypdohk xh6dtrn x1a2a7pz x1p25gnr x1y3gkto"
          },
          1: {
            className: "x78zum5 x6s0dn4 xl56j7k x1717udv x1ghz6dp xc342km xng3xce xjbqb8w xh6dtrn x1a2a7pz x1p25gnr x1y3gkto x1h6gzvc"
          }
        }[!!isEffectivelyDisabled << 0],
        children: /*#__PURE__*/_jsx(Icon, {
          icon: "calendar",
          size: "sm",
          color: "secondary"
        })
      }), /*#__PURE__*/_jsx("button", {
        ref: ref,
        id: id,
        type: "button",
        onClick: handleToggle
        // With a disabledMessage the trigger keeps focusability via
        // aria-disabled so the reason is focus-discoverable; activation is
        // still blocked by the isEffectivelyDisabled guard in handleToggle.
        ,
        disabled: isEffectivelyDisabled && !showsDisabledMessage,
        "aria-disabled": showsDisabledMessage ? 'true' : undefined,
        "aria-label": triggerAriaLabel,
        "aria-describedby": ariaDescribedBy,
        "aria-required": isRequired === true ? 'true' : undefined,
        "aria-invalid": status?.type === 'error' ? 'true' : undefined,
        "aria-busy": isBusy || undefined,
        "aria-expanded": popover.isOpen,
        "aria-haspopup": "dialog",
        "aria-controls": popover.isOpen ? popover.id : undefined,
        ...{
          0: {
            className: "x1lliihq x98rzlu xeuugli xc342km xng3xce x1717udv x9ynric xjm74w1 x6pjikd xw6l6zx x1tgivj0 xjbqb8w x1a2a7pz x1ypdohk x1yc453h xuxw1ft xb3r6kr xlyipyv"
          },
          2: {
            className: "x1lliihq x98rzlu xeuugli xc342km xng3xce x1717udv x9ynric xjm74w1 x6pjikd xw6l6zx xjbqb8w x1a2a7pz x1ypdohk x1yc453h xuxw1ft xb3r6kr xlyipyv xv1l7n4"
          },
          1: {
            className: "x1lliihq x98rzlu xeuugli xc342km xng3xce x1717udv x9ynric xjm74w1 x6pjikd xw6l6zx x1tgivj0 xjbqb8w x1a2a7pz x1yc453h xuxw1ft xb3r6kr xlyipyv x1h6gzvc"
          },
          3: {
            className: "x1lliihq x98rzlu xeuugli xc342km xng3xce x1717udv x9ynric xjm74w1 x6pjikd xw6l6zx xjbqb8w x1a2a7pz x1yc453h xuxw1ft xb3r6kr xlyipyv xv1l7n4 x1h6gzvc"
          }
        }[!!!displayValue << 1 | !!isEffectivelyDisabled << 0],
        children: displayValue || placeholder
      }), hasClear && value !== null && !isEffectivelyDisabled && /*#__PURE__*/_jsx("button", {
        type: "button",
        onClick: handleClear,
        "aria-label": t('@astryx.dateInput.clear', {
          label
        }),
        ...{
          className: "x78zum5 x6s0dn4 xl56j7k x1717udv x1ghz6dp xc342km xng3xce xjbqb8w x1ypdohk xh6dtrn x1a2a7pz x1p25gnr x1y3gkto"
        },
        children: /*#__PURE__*/_jsx(Icon, {
          icon: "close",
          size: "sm",
          color: "secondary"
        })
      }), isBusy && /*#__PURE__*/_jsx(Spinner, {
        size: "sm"
      }), status && /*#__PURE__*/_jsx(Icon, {
        icon: statusIconMap[status.type],
        size: "md",
        color: statusIconColorMap[status.type]
      })]
    }), popover.render(/*#__PURE__*/_jsxs("div", {
      ...{
        className: "x78zum5"
      },
      children: [presets && presets.length > 0 && /*#__PURE__*/_jsx("div", {
        role: "group",
        "aria-label": t('@astryx.dateRangeInput.presetDateRanges'),
        ...{
          className: "x78zum5 xdt5ytf xzye2dw x1b2ylru x1pcaw5z x32b0ac xdz7fjg x1d77m7x"
        },
        children: presets.map(preset => {
          const presetRange = preset.getRange();
          const isActive = isRangeEqual(value, presetRange);
          return /*#__PURE__*/_jsx("button", {
            type: "button"
            // These presets are independent action buttons navigated by
            // Tab, not a single-tab-stop listbox — so they are a labeled
            // group of buttons, and the currently-applied preset is
            // marked with aria-current (not aria-selected, a listbox
            // concept that contradicted the Tab interaction) (forms-5).
            ,
            "aria-current": isActive ? 'true' : undefined,
            onClick: () => handlePresetClick(preset),
            ...{
              0: {
                className: "x1lliihq xh8yej3 xtozwh x1ghz6dp xc342km xng3xce xh6dtrn xjbqb8w xe9uy6x x9ynric xcr08ib x1kq96og x1tgivj0 x1ypdohk x1yc453h x1a2a7pz x1p25gnr"
              },
              1: {
                className: "x1lliihq xh8yej3 xtozwh x1ghz6dp xc342km xng3xce xh6dtrn x9ynric xcr08ib x1kq96og x1ypdohk x1yc453h x1a2a7pz x1p25gnr xgcxg3y xqwr325"
              }
            }[!!isActive << 0],
            children: preset.label
          }, preset.label);
        })
      }), /*#__PURE__*/_jsx(Calendar, {
        mode: "range",
        value: value ?? undefined,
        onChange: handleRangeSelect,
        min: min,
        max: max,
        dateConstraints: dateConstraints,
        numberOfMonths: numberOfMonths
      })]
    }), {
      placement: 'below',
      alignment: 'start'
    }), showsDisabledMessage && disabledMessageTooltip.renderTooltip(disabledMessage)]
  });
}
DateRangeInput.displayName = 'DateRangeInput';